import Dialog from "../../General/ConfirmableDialog";
import React, { FC, memo, useEffect } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  MenuItem,
  Autocomplete,
  Box,
  Switch,
  FormControlLabel,
  CircularProgress,
  Typography,
} from "@mui/material";
import { useForm, Controller, UseFormSetValue } from "react-hook-form";
import {
  GenericFormPopupProps,
  OptionType,
} from "../../../interfaces/Fleet/IGenericFormPopupProps";

interface GenericFormPopupPropsWithLoading extends GenericFormPopupProps {
  loading: boolean;
  includeApproval?: boolean;
  shouldReset?: boolean;
  fieldComponents?: Record<string, React.ReactNode>;
  onValuesChange?: (values: any, setValue?: UseFormSetValue<any>) => void;
}

const GenericFormPopup: FC<GenericFormPopupPropsWithLoading> = memo(
  ({
    formFields,
    initialValues,
    title,
    onSubmit,
    open,
    onClose,
    autocompleteData,
    additionalComponents,
    loading,
    includeApproval = false,
    fieldComponents = {},
    onValuesChange,
  }) => {
    const {
      control,
      register,
      handleSubmit,
      reset,
      setValue,
      watch,
      formState: { errors, isValid, isDirty },
    } = useForm({
      defaultValues: initialValues,
      mode: "onChange",
      reValidateMode: "onChange",
    });
    const { trigger } = useForm({
      defaultValues: initialValues,
      mode: "onChange",
      reValidateMode: "onChange",
    });

    useEffect(() => {
      const subscription = watch((values) => {
        if (onValuesChange) {
          onValuesChange(values, setValue);
        }
      });
      return () => subscription.unsubscribe();
    }, [watch, onValuesChange, setValue]);

    useEffect(() => {
      if (open) {
        reset(initialValues);
      }
    }, [open, initialValues, reset]);

    const requiresApproval = includeApproval
      ? watch("requiresApproval", initialValues.requiresApproval)
      : false;

    useEffect(() => {
      if (includeApproval && !requiresApproval) {
        setValue("completedWithoutApproval", false);
      }
    }, [requiresApproval, setValue, includeApproval]);

    const handleRequiresApprovalChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const checked = event.target.checked;
      setValue("requiresApproval", checked);
      if (!checked) {
        setValue("completedWithoutApproval", false);
      }
    };

    const handleCompletedWithoutApprovalChange = (
      event: React.ChangeEvent<HTMLInputElement>,
    ) => {
      const checked = event.target.checked;
      setValue("completedWithoutApproval", checked);
    };

    const handleClose = () => {
      onClose();
      reset();
    };

    return (
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle
          sx={{ textAlign: "center", fontWeight: "bold", fontSize: "1.5rem" }}
        >
          {title}
        </DialogTitle>
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            sx={{ mt: 2 }}
          >
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Fields with <span style={{ color: "red" }}>*</span> are mandatory
            </Typography>
            {formFields.map(
              ({
                name,
                label,
                type,
                options,
                validation,
                placeholder,
                dataKey,
                onChange,
                disabled,
              }) => {
                const isRequired = Boolean(validation && validation.required);
                const labelWithAsterisk = isRequired ? `${label} *` : label;
                if (type === "select" && options) {
                  return (
                    <Controller
                      name={name}
                      control={control}
                      defaultValue={initialValues[name] || ""}
                      rules={validation}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label={labelWithAsterisk}
                          fullWidth
                          margin="normal"
                          variant="outlined"
                          error={Boolean(errors[name])}
                          helperText={errors[name]?.message?.toString()}
                          disabled={disabled}
                          onChange={(e) => {
                            field.onChange(e.target.value);
                            if (onChange) onChange(e);
                            trigger(name);
                          }}
                        >
                          {options.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  );
                } else if (type === "combo") {
                  return (
                    <Box key={name}>
                      {autocompleteData && dataKey && (
                        <Controller
                          name={name}
                          control={control}
                          defaultValue={initialValues[name] || null}
                          rules={validation}
                          render={({ field }) => (
                            <Autocomplete
                              options={
                                (autocompleteData[dataKey] as OptionType[]) ||
                                []
                              }
                              getOptionLabel={(option) => option.label}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value?.id
                              }
                              onChange={(event, newValue) => {
                                field.onChange(newValue);
                                trigger(name);
                              }}
                              value={field.value || null}
                              disabled={disabled}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label={labelWithAsterisk}
                                  margin="normal"
                                  variant="outlined"
                                  error={Boolean(errors[name])}
                                  helperText={errors[name]?.message?.toString()}
                                  disabled={disabled}
                                />
                              )}
                            />
                          )}
                        />
                      )}
                      {fieldComponents[name] && (
                        <Box>{fieldComponents[name]}</Box>
                      )}
                    </Box>
                  );
                } else if (type === "switch") {
                  return (
                    <FormControlLabel
                      key={name}
                      control={
                        <Controller
                          name={name}
                          control={control}
                          defaultValue={initialValues[name]}
                          render={({ field }) => (
                            <Switch
                              {...field}
                              checked={field.value}
                              onChange={
                                name === "requiresApproval"
                                  ? handleRequiresApprovalChange
                                  : handleCompletedWithoutApprovalChange
                              }
                              color="primary"
                              disabled={
                                name === "completedWithoutApproval" &&
                                !requiresApproval
                              }
                            />
                          )}
                        />
                      }
                      label={label}
                    />
                  );
                } else {
                  return (
                    <TextField
                      key={name}
                      {...register(name, validation)}
                      label={labelWithAsterisk}
                      type={type}
                      fullWidth
                      margin="normal"
                      variant="outlined"
                      placeholder={placeholder}
                      value={watch(name, initialValues[name]) || ""}
                      InputLabelProps={
                        type === "date" || type === "datetime-local"
                          ? { shrink: true }
                          : undefined
                      }
                      error={Boolean(errors[name])}
                      helperText={errors[name]?.message?.toString()}
                      disabled={disabled}
                    />
                  );
                }
              },
            )}
            {includeApproval && (
              <>
                <FormControlLabel
                  control={
                    <Controller
                      name="requiresApproval"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value}
                          onChange={handleRequiresApprovalChange}
                          color="primary"
                        />
                      )}
                    />
                  }
                  label="Requires Approval"
                />
                <FormControlLabel
                  control={
                    <Controller
                      name="completedWithoutApproval"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value}
                          onChange={handleCompletedWithoutApprovalChange}
                          color="primary"
                          disabled={!requiresApproval}
                        />
                      )}
                    />
                  }
                  label="Completed Without Approval"
                />
              </>
            )}
            {additionalComponents && (
              <Box
                sx={{ textAlign: "center", mt: 2 }}
                key="additional-components"
              >
                {additionalComponents}
              </Box>
            )}
            <DialogActions sx={{ justifyContent: "center", mt: 2 }}>
              <Button
                onClick={handleClose}
                color="secondary"
                variant="outlined"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={!isDirty || !isValid || loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? "Submitting..." : "Submit"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
    );
  },
);

export default GenericFormPopup;
